import { dequal as deepEqual } from 'dequal/lite';
import get from 'lodash/fp/get';
import xorWith from 'lodash/fp/xorWith';
import type { Replace } from 'type-fest';
import type { TagName } from '#dn-types/host.ts';
import { localAllowListSchema, remoteAllowListSchema } from '#schemas/config-override/index.ts';
import type { OverridePlusSource } from '../../../hooks/useOverrideMaps.ts';
import { mapOverridesToForm } from '../mappers/mapOverridesToForm.ts';
import type { LocalResetArgs, RemoteResetArgs } from './AllowList.tsx';
import type { AllowListItem } from './AllowRule.tsx';

/** {@link checkIsAllowListFromTag} takes a list of allow list items and determines if they match the value provided by a tag in the {@link overrideMap} */
export function checkIsAllowListFromTag<
  const T extends 'configOverrides.lighthouse.local_allow_list' | 'configOverrides.lighthouse.remote_allow_list',
>({ name, items, overrideMap }: { name: T; items: AllowListItem[]; overrideMap: Map<string, OverridePlusSource> }) {
  let isValueFromTag = false;
  const path = name.replace('configOverrides.', '') as Replace<
    Extract<typeof name, `configOverrides.${string}`>,
    'configOverrides.',
    ''
  >;
  const override = overrideMap.get(path);

  // If the source is the host, we know that nothing can be from a tag
  if (!override || override.source === 'host') {
    return {
      tagName: undefined,
      isValueFromTag: false,
      tagProvidedValue: undefined,
    };
  }

  const tagName = override.source;
  const tagProvidedValueAsFormValue = (
    name === 'configOverrides.lighthouse.local_allow_list' ?
      localAllowListSchema.parse(get(path, mapOverridesToForm([override.value])))
    : remoteAllowListSchema.parse(get(path, mapOverridesToForm([override.value])))) as T extends (
    'configOverrides.lighthouse.local_allow_list'
  ) ?
    LocalResetArgs
  : RemoteResetArgs;

  if (
    xorWith(
      (a, b) => {
        // Ignore the ID when comparing
        return deepEqual({ ...a, id: undefined }, { ...b, id: undefined });
      },
      tagProvidedValueAsFormValue,
      items
    ).length === 0
  ) {
    isValueFromTag = true;
  }

  return {
    tagName,
    isValueFromTag,
    tagProvidedValue: tagProvidedValueAsFormValue,
  } satisfies {
    tagName: TagName;
    isValueFromTag: boolean;
    tagProvidedValue: T extends 'configOverrides.lighthouse.local_allow_list' ? LocalResetArgs : RemoteResetArgs;
  };
}
